import { StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  MetricCard,
  ProgressBar,
  ProtectedScreen,
  SectionHeader,
  Stack,
  StatMeter,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import {
  useAlertActions,
  useAlerts,
  useListAnalytics,
} from "~/state/analytics";
import { useTasks } from "~/state/tasks";
import { SUBSCRIPTION_PATH } from "~/utils/onboarding";
import { spacing } from "~/styles/theme";

export default function AnalyticsScreen() {
  const router = useRouter();
  const plan = usePlan();
  const analytics = useListAnalytics();
  const tasks = useTasks();
  const alerts = useAlerts();
  const alertActions = useAlertActions();

  if (!plan.canUseAnalytics) {
    return (
      <ProtectedScreen
        title="Analytics"
        subtitle="Completion rate, streak, and how regularly each task gets done."
      >
        <EmptyState
          title="Analytics is part of Pro"
          description="Upgrade to see 30- and 90-day completion rates, streaks, and incomplete-task alerts."
          action={
            <Button onPress={() => router.push(SUBSCRIPTION_PATH as never)}>
              <ButtonText>See TaskScribe Pro</ButtonText>
            </Button>
          }
        />
        <UpgradePrompt title="Unlock analytics and alerts" />
      </ProtectedScreen>
    );
  }

  const dailyAlert = alerts.find((alert) => alert.kind === "incomplete_daily");

  return (
    <ProtectedScreen
      title="Analytics"
      subtitle="How the lists are actually getting done."
    >
      <StatMeter
        label="30-day completion"
        value={analytics.rate30}
        caption={`${analytics.completed30} of ${analytics.expected30} expected completions in the last 30 days`}
        tone={analytics.rate30 >= 0.6 ? "primary" : "danger"}
      />

      <View style={stylesheet.metrics}>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Completions / expected"
            value={`${analytics.completed30} / ${analytics.expected30}`}
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Longest streak"
            value={String(analytics.longestStreak)}
          />
        </View>
      </View>

      <Card gap="md">
        <SectionHeader
          title="By task"
          description="How regularly each repeating task was completed."
        />
        {analytics.perTask.length === 0 ? (
          <EmptyState title="No tasks yet" />
        ) : (
          <Stack gap="sm">
            {analytics.perTask.map((stats) => {
              const task = tasks.find((item) => item.id === stats.taskId);
              if (!task) return null;
              const strong = stats.rate30 >= 0.8;
              return (
                <ListRow
                  key={stats.taskId}
                  title={task.title}
                  description={
                    <View style={stylesheet.taskMeter}>
                      <ProgressBar
                        value={stats.rate30}
                        tone={strong ? "primary" : "danger"}
                      />
                      <AppText size="xs" tone="muted">
                        {`${Math.round(stats.rate30 * 100)}% in 30 days · streak ${stats.streak}`}
                      </AppText>
                    </View>
                  }
                  onPress={() => router.push(`/tasks/${task.id}` as never)}
                  meta={
                    <Badge tone={strong ? "success" : "warning"}>
                      {stats.completed30}/{stats.expected30}
                    </Badge>
                  }
                />
              );
            })}
          </Stack>
        )}
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Incomplete-task alerts"
          description="A nudge at 6pm if something due today is still open. Push and in-app."
        />
        <AppText size="xs">
          {dailyAlert?.enabled
            ? `Daily incomplete alert is on, around ${dailyAlert.hour_local}:00 local time.`
            : "Daily incomplete alert is off."}
        </AppText>
        <Button
          align="start"
          variant="outline"
          onPress={() =>
            alertActions.upsert("incomplete_daily", 18, !dailyAlert?.enabled)
          }
        >
          <ButtonText>
            {dailyAlert?.enabled
              ? "Turn daily alert off"
              : "Turn daily alert on"}
          </ButtonText>
        </Button>
      </Card>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 160, flex: 1 },
  taskMeter: { gap: spacing.xs, marginTop: spacing.xs },
});
